// src/components/DashboardPage.js
import React, { useState, useEffect } from "react"
import { Navigate, Link } from "react-router-dom"
import { auth, database } from "../firebase"
import { ref, get, set } from "firebase/database"
import appIcon from '../assets/icon.png'
import userJourneyTracker from "../hooks/user-journey-tracker";

const DashboardPage = () => {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [subscriptionStatus, setSubscriptionStatus] = useState(null)
    const [stripeCustomerId, setStripeCustomerId] = useState(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [showDownloadModal, setShowDownloadModal] = useState(false)
    // New state for Android waitlist modal
    const [showAndroidModal, setShowAndroidModal] = useState(false)
    // New state for showing success message
    const [showWaitlistSuccess, setShowWaitlistSuccess] = useState(false)
    // Pricing tier selection
    const [selectedTier, setSelectedTier] = useState('monthly')

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                setUser(user);
                // Start tracking user journey
                await userJourneyTracker.initialize();
                userJourneyTracker.trackStep("DashboardPage", "ViewDashboard");
                
                const userRef = ref(database, `users/${user.uid}`)
                const snapshot = await get(userRef)
                const userData = snapshot.val()
                if (userData) {
                    setSubscriptionStatus(userData.subscriptionStatus)
                    setStripeCustomerId(userData.stripeCustomerId)
                }
                
                // Also make sure user is recorded in metrics for Today's users if they're new
                const initialClicksRef = ref(database, `metrics/initialClicks/${user.uid}`);
                const initialClicksSnapshot = await get(initialClicksRef);
                
                if (!initialClicksSnapshot.exists()) {
                    // New user - create the initial clicks entry with metadata
                    await set(initialClicksRef, {
                        email: user.email || null,
                        displayName: user.displayName || null,
                        creationTime: Date.now(), // Current timestamp for today's users filtering
                        completed: false,
                        clicks: {}
                    });
                }
            } else {
                setUser(null)
            }
            setLoading(false)
        })
    
        return () => {
            unsubscribe();
            // End tracking session when component unmounts
            userJourneyTracker.endSession();
        };
    }, []);
    
    const handleLogout = () => {
        userJourneyTracker.trackStep("DashboardPage", "Logout");

        // Clear the modal session flag when user logs out
        localStorage.removeItem('hasSeenAppModal');
        userJourneyTracker.endSession();

        // Perform the actual logout
        auth.signOut()
            .then(() => {
                console.log("User signed out successfully");
                // Navigate to login or home page if needed
                // navigate('/login');
            })
            .catch((error) => {
                console.error("Error signing out:", error);
            });
    }
    
    const handleSubscribe = async () => {
        try {
            userJourneyTracker.trackStep("DashboardPage", "StartFreeTrial");

            console.log("Starting checkout process...")
            const response = await fetch("/.netlify/functions/create-checkout", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    userId: user.uid,
                    pricingTier: selectedTier,
                }),
            })
            const data = await response.json()

            if (data.url) {
                window.location.href = data.url
            } else {
                console.error("No URL in response:", data)
            }
        } catch (error) {
            console.error("Error during checkout:", error)
            userJourneyTracker.trackStep("DashboardPage", "CheckoutError", { error: error.message });

        }
    }

    const handleManageSubscription = async () => {
        try {
            userJourneyTracker.trackStep("DashboardPage", "ManageSubscription");

            console.log("Creating portal session...", stripeCustomerId)
            const response = await fetch("/.netlify/functions/create-portal", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    stripeCustomerId: stripeCustomerId,
                }),
            })

            const data = await response.json()

            if (data.url) {
                window.location.href = data.url
            } else {
                console.error("No portal URL returned:", data)
            }
        } catch (error) {
            console.error("Error creating portal session:", error)

        }
    }

    const handleIOSDownload = () => {
        userJourneyTracker.trackStep("DashboardPage", "DownloadIOSApp");

        // Redirect to iOS App Store
        window.location.href = "https://apps.apple.com/us/app/forecast-financial-planner/id6742382846";
    }

    const handleAndroidDownload = () => {
        userJourneyTracker.trackStep("DashboardPage", "ShowAndroidWaitlist");
        // Show the Android waitlist modal instead of redirecting
        setShowAndroidModal(true);
    }

    const handleAddToWaitlist = async () => {
        try {
            // Track this event
            userJourneyTracker.trackStep("DashboardPage", "JoinAndroidWaitlist");
            
            // Add user to the waitlist in Firebase
            const waitlistRef = ref(database, `waitlist/${user.uid}`);
            await set(waitlistRef, {
                email: user.email,
                timestamp: Date.now(),
                platform: 'android'
            });
            
            // Close the modal and show success message
            setShowAndroidModal(false);
            setShowWaitlistSuccess(true);
            
            // Hide success message after 3 seconds
            setTimeout(() => {
                setShowWaitlistSuccess(false);
            }, 3000);
            
            console.log("User added to Android waitlist:", user.uid);
        } catch (error) {
            console.error("Error adding to waitlist:", error);
        }
    }

    if (loading) {
        return <div>Loading...</div>
    }

    if (!user) {
        return <Navigate to="/" />
    }

    return (
        <div style={{ 
            minHeight: "100vh", 
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
        }}>
            {/* Navigation header with icon, app name, and hamburger menu */}
            <nav style={{
                backgroundColor: "white",
                padding: "1rem",
                width: "100%",
                marginBottom: "2rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}>
                <div style={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                    }}>
                        <img 
                            src={appIcon} 
                            alt="Forecast App Icon" 
                            style={{
                                width: '32px',
                                height: '32px',
                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                            }}
                        />
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                            Forecast
                        </span>
                    </div>
                    
                    {/* Right side menu items with hamburger only (Download App button removed) */}
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "1rem"
                    }}>
                        {/* Hamburger Menu */}
                        <div style={{ position: "relative" }}>
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "0.5rem",
                                }}
                                aria-label="Menu"
                            >
                                {/* Hamburger Icon */}
                                <div
                                    style={{
                                        width: "24px",
                                        height: "3px",
                                        backgroundColor: "#333",
                                        marginBottom: "5px",
                                        borderRadius: "1px",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        width: "24px",
                                        height: "3px",
                                        backgroundColor: "#333",
                                        marginBottom: "5px",
                                        borderRadius: "1px",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        width: "24px",
                                        height: "3px",
                                        backgroundColor: "#333",
                                        borderRadius: "1px",
                                    }}
                                ></div>
                            </button>

                            {/* Dropdown Menu */}
                            {menuOpen && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "100%",
                                        right: "0",
                                        backgroundColor: "white",
                                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                                        borderRadius: "0.375rem",
                                        padding: "0.5rem",
                                        zIndex: 1000,
                                        minWidth: "160px",
                                    }}
                                >
                                    <Link
                                        to="/calendar"
                                        style={{
                                            display: "block",
                                            textDecoration: "none",
                                            color: "#2563eb",
                                            padding: "0.5rem 1rem",
                                            borderRadius: "0.375rem",
                                        }}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        Calendar
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content - Profile Style Layout */}
            <div style={{
                maxWidth: "500px",
                width: "90%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "2rem"
            }}>
                {/* User Profile Circle with Initial */}
                <div style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    backgroundColor: "#e2e8f0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "1.5rem"
                }}>
                    <span style={{
                        fontSize: "2.5rem",
                        fontWeight: "bold",
                        color: "#475569"
                    }}>
                        {user.email ? user.email.charAt(0).toUpperCase() : "U"}
                    </span>
                </div>
                
                {/* User Email */}
                <span style={{
                    fontSize: "1.125rem",
                    color: "#333",
                    marginBottom: "2rem"
                }}>
                    {user.email}
                </span>
                
                {/* Subscription Status */}
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginBottom: "2rem",
                    width: "100%"
                }}>
                    <h2 style={{
                        fontSize: "1.25rem",
                        fontWeight: "600",
                        margin: "0 0 0.75rem 0",
                        color: "#1e293b"
                    }}>
                        Subscription Status
                    </h2>
                    <span style={{
                        fontSize: "1.125rem",
                        marginBottom: "1.5rem",
                        color: "#333"
                    }}>
                        {subscriptionStatus === "active" ? "Active" : "Inactive"}
                    </span>

                    {/* Pricing tier selector */}
                    {subscriptionStatus !== "active" && (
                        <div style={{
                            display: "flex",
                            gap: "1rem",
                            marginBottom: "1.5rem",
                            width: "100%",
                            maxWidth: "300px"
                        }}>
                            <button
                                onClick={() => setSelectedTier('monthly')}
                                style={{
                                    flex: 1,
                                    padding: "1rem",
                                    border: selectedTier === 'monthly' ? "2px solid #007AFF" : "1px solid #e2e8f0",
                                    borderRadius: "0.5rem",
                                    backgroundColor: selectedTier === 'monthly' ? "#eff6ff" : "white",
                                    cursor: "pointer",
                                    textAlign: "center"
                                }}
                            >
                                <div style={{fontWeight: "600", fontSize: "1rem", color: "#1e293b", marginBottom: "0.25rem"}}>
                                    Monthly
                                </div>
                                <div style={{fontSize: "1.25rem", fontWeight: "700", color: "#007AFF"}}>
                                    $9.99
                                </div>
                                <div style={{fontSize: "0.75rem", color: "#64748b"}}>
                                    per month
                                </div>
                            </button>
                            <button
                                onClick={() => setSelectedTier('yearly')}
                                style={{
                                    flex: 1,
                                    padding: "1rem",
                                    border: selectedTier === 'yearly' ? "2px solid #007AFF" : "1px solid #e2e8f0",
                                    borderRadius: "0.5rem",
                                    backgroundColor: selectedTier === 'yearly' ? "#eff6ff" : "white",
                                    cursor: "pointer",
                                    textAlign: "center",
                                    position: "relative"
                                }}
                            >
                                <div style={{
                                    position: "absolute",
                                    top: "-8px",
                                    right: "-8px",
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    fontSize: "0.625rem",
                                    fontWeight: "600",
                                    padding: "0.25rem 0.5rem",
                                    borderRadius: "0.25rem"
                                }}>
                                    SAVE 33%
                                </div>
                                <div style={{fontWeight: "600", fontSize: "1rem", color: "#1e293b", marginBottom: "0.25rem"}}>
                                    Yearly
                                </div>
                                <div style={{fontSize: "1.25rem", fontWeight: "700", color: "#007AFF"}}>
                                    $79.99
                                </div>
                                <div style={{fontSize: "0.75rem", color: "#64748b"}}>
                                    per year
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Pricing message */}
                    {subscriptionStatus !== "active" && (
                        <p style={{
                            fontSize: "0.875rem",
                            color: "#64748b",
                            margin: "0 0 1.5rem 0",
                            textAlign: "center",
                            maxWidth: "300px"
                        }}>
                            14-day free trial • Cancel anytime
                        </p>
                    )}
                    
                    {/* Subscription Button */}
                    <button
                        onClick={subscriptionStatus === "active" ? handleManageSubscription : handleSubscribe}
                        style={{
                            padding: "0.85rem 1.5rem",
                            backgroundColor: "#007AFF",
                            color: "white",
                            border: "none",
                            borderRadius: "0.5rem",
                            cursor: "pointer",
                            fontWeight: "700",
                            fontSize: "1rem",
                            width: "100%",
                            maxWidth: "300px",
                            marginBottom: "1.5rem"
                        }}
                    >
                        {subscriptionStatus === "active" ? "Manage Subscription" : "Start 7-day Free Trial"}
                    </button>
                    
                    {/* Waitlist Success Message */}
                    {showWaitlistSuccess && (
                        <div style={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            padding: "0.75rem 1rem",
                            borderRadius: "0.5rem",
                            marginBottom: "1.5rem",
                            textAlign: "center",
                            width: "100%",
                            maxWidth: "300px"
                        }}>
                            You've been added to the Android waitlist!
                        </div>
                    )}
                
                    {/* Download app buttons */}
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        marginBottom: "1.5rem",
                        width: "100%",
                        maxWidth: "300px"
                    }}>
                        {/* iOS Download Button */}
                        <button
                            onClick={handleIOSDownload}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.75rem",
                                padding: "0.75rem 1rem",
                                backgroundColor: "black",
                                color: "white",
                                border: "none",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                fontWeight: "500",
                                fontSize: "1rem"
                            }}
                        >
                            {/* Apple Logo SVG */}
                            <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ marginRight: "0.5rem" }}
                            >
                                <path 
                                    d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09998 22C7.78998 22.05 6.79998 20.68 5.95998 19.47C4.24998 17 2.93998 12.45 4.69998 9.39C5.56998 7.87 7.12998 6.91 8.81998 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.09 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" 
                                    fill="white"
                                />
                            </svg>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start"
                            }}>
                                <span style={{ fontSize: "0.7rem" }}>Download on the</span>
                                <span style={{ fontSize: "1.1rem", fontWeight: "600" }}>App Store</span>
                            </div>
                        </button>
                        
                        {/* Android Download Button */}
                        <button
                            onClick={handleAndroidDownload}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.75rem",
                                padding: "0.75rem 1rem",
                                backgroundColor: "#3DDC84",
                                color: "white",
                                border: "none",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                fontWeight: "500",
                                fontSize: "1rem"
                            }}
                        >
                            {/* Google Play Logo SVG */}
                            <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ marginRight: "0.5rem" }}
                            >
                                <path 
                                    d="M3.18 20.83C3.0633 20.6891 2.99937 20.5246 3 20.36V3.64001C3 3.47001 3.06 3.31001 3.18 3.17001L13.36 12L3.18 20.83Z" 
                                    fill="white"
                                />
                                <path 
                                    d="M16.62 8.99001C16.3664 8.8413 16.0855 8.74681 15.79 8.71001L15.54 8.58001L13.36 12L16.24 15.41L20.92 12.84C19.49 11.99 17.97 10.9 16.62 8.99001Z" 
                                    fill="white"
                                />
                                <path 
                                    d="M13.36 12L3.18 3.17001C3.28 3.03001 3.42 3 3.6 3C3.83 3 4.07 3.08001 4.32 3.21001L13.36 8.48001L15.54 8.58001L13.36 12Z" 
                                    fill="white"
                                />
                                <path 
                                    d="M13.36 15.52L4.32 20.79C4.07 20.92 3.83 21 3.6 21C3.42 21 3.28 20.97 3.18 20.83L13.36 12L16.24 15.41L13.36 15.52Z" 
                                    fill="white"
                                />
                            </svg>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start"
                            }}>
                                <span style={{ fontSize: "0.7rem" }}>GET IT ON</span>
                                <span style={{ fontSize: "1.1rem", fontWeight: "600" }}>Google Play</span>
                            </div>
                        </button>
                    </div>
                </div>
                
                {/* Log Out Button */}
                <button
                    onClick={handleLogout}
                    style={{
                        padding: "0.75rem 1.5rem",
                        backgroundColor: "transparent",
                        color: "#333",
                        border: "none",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        fontWeight: "500",
                        fontSize: "1rem",
                        marginTop: "1rem"
                    }}
                >
                    Log Out
                </button>
            </div>

            {/* Android Waitlist Modal */}
            {showAndroidModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "0.5rem",
                        padding: "1.5rem",
                        maxWidth: "90%",
                        width: "360px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                    }}>
                        <h3 style={{
                            fontSize: "1.25rem",
                            fontWeight: "600",
                            marginBottom: "1rem",
                            color: "#1e293b"
                        }}>
                            Android App Coming Soon
                        </h3>
                        
                        <p style={{
                            fontSize: "1rem",
                            color: "#4b5563",
                            marginBottom: "1.5rem"
                        }}>
                            Our Android app is currently in development. Would you like to be notified when it's available for download?
                        </p>
                        
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "0.75rem"
                        }}>
                            <button
                                onClick={() => setShowAndroidModal(false)}
                                style={{
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "transparent",
                                    color: "#6b7280",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "0.375rem",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            
                            <button
                                onClick={handleAddToWaitlist}
                                style={{
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "#3DDC84",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "0.375rem",
                                    cursor: "pointer",
                                    fontWeight: "500"
                                }}
                            >
                                Notify Me
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DashboardPage